import { useCallback, useEffect, useMemo, useState, lazy } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import { setBaseUrl } from '@tecma/bucket-lib';
import { loadResources, useTranslation } from '@tecma/i18n';
import Cookies from 'js-cookie';
import moment from 'moment';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';

import { sliceClienti } from '@module-clients/redux/selectors/slice.clienti';
import convertLanguage from 'helpers/functions/convertLanguage';
import DetectUrl from 'helpers/functions/DetectUrl';
import { forceLogoutUnauthorizedUser } from 'helpers/functions/forceLogoutUnauthorizedUser';
import JwtChecker from 'helpers/functions/JwtChecker.js';
import Client from 'helpers/queries/query.client';
import { useStore } from 'mobx/helpers';

import LogoutListener from 'components/Auth.Logout';
import PageLogin from 'components/Page.Login';
import PrivateRoute from 'components/Route.Logged';
import SessionStore from 'components/Session';
import CrmSnackbarAmber from 'components/Snackbar.Amber';
import CrmSnackbarError from 'components/Snackbar.Error';
import CrmSnackbarFeedback from 'components/Snackbar.Feedback';
import { usePageTitleOverride } from 'components/Template.Page';
import CrmLoader from 'components/UI.Loader';

import { APARTMENTS_VERSION_COOKIE } from 'constants/apartments';
import { ENVIRONMENT } from 'constants/environment';

import { useAppDispatch } from './redux/hooks';

const PageApartmentCreate = lazy(() => import('components/Page.Apartment.Create'));
const PageApartmentListLegacy = lazy(() => import('components/Page.Apartment.List'));
const PageApartmentListV2 = lazy(() => import('components/Page.Apartment.List.V2'));
const PageApparmentUpdate = lazy(() => import('components/Page.Apartment.Update'));
const AggiornaConfermaPassword = lazy(() => import('components/Page.Auth.Password.Confirm'));
const PageCalendar = lazy(() => import('components/Page.Calendar'));
const PageCalendarDetail = lazy(() => import('components/Page.Calendar.Detail'));
const PageCalendarSettings = lazy(() => import('components/Page.Calendar.Settings.jsx'));
const PageClientCreate = lazy(() => import('components/Page.Client.Create'));
const PageClientDetail = lazy(() => import('components/Page.Client.Detail'));
const PageClientUpdate = lazy(() => import('components/Page.Client.Update'));
const PageClientListLegacy = lazy(() => import('components/Page.Clienti'));
const PageClients = lazy(() => import('components/Page.Clienti.V2'));
const PageDocuments = lazy(() => import('components/Page.Documents'));
const PageEventCreate = lazy(() => import('components/Page.Event.Create'));
const PageEventUpdate = lazy(() => import('components/Page.Event.Update'));
const PageHome = lazy(() => import('components/Page.Home'));
const PagePertinenceUpdate = lazy(() => import('components/Page.Pertinence.Update'));
const PageProposalManagement = lazy(() => import('components/Page.Proposal.Management'));
const PageQuoteList = lazy(() => import('components/Page.Quotes'));
const PageSlotUpdate = lazy(() => import('components/Page.Slot.Update'));
const PageUserDetail = lazy(() => import('components/Page.User.Detail'));
const PageUserManagement = lazy(() => import('components/Page.User.Management'));

type ApartmentsUiVersion = 'legacy' | 'v2';
type ApartmentsVersionEventListener = (event: Event) => void;

const getInitialApartmentsVersion = (): ApartmentsUiVersion => {
  const cookieValue = Cookies.get(APARTMENTS_VERSION_COOKIE);
  if (cookieValue === 'legacy' || cookieValue === 'v2') {
    return cookieValue;
  }

  const flag = ENVIRONMENT.REACT_APP_APARTMENTS_UI_V2;
  const prefersV2 = Boolean(flag);

  return prefersV2 ? 'v2' : 'legacy';
};

const ApartmentsPageSwitch = (props) => {
  const [version, setVersion] = useState<ApartmentsUiVersion>(() => getInitialApartmentsVersion());
  const { t } = useTranslation();

  const updateVersion = useCallback((nextVersion: ApartmentsUiVersion) => {
    Cookies.set(APARTMENTS_VERSION_COOKIE, nextVersion, { expires: 365, path: '/' });
    setVersion(nextVersion);
  }, []);

  // handle used to switch between legacy and v2 ui of apartments page
  useEffect(() => {
    const handler = (event: Event) => {
      const { detail } = event as CustomEvent<ApartmentsUiVersion>;
      if (detail === 'legacy' || detail === 'v2') {
        updateVersion(detail);
      }
    };

    window.addEventListener('apartments:setVersion', handler as ApartmentsVersionEventListener);

    return () => {
      window.removeEventListener('apartments:setVersion', handler as ApartmentsVersionEventListener);
    };
  }, [updateVersion]);

  const isV2 = version === 'v2';

  const titleOverrideNode = useMemo(() => {
    if (!isV2) return null;
    return (
      <div className='page-title__content'>
        <span className='page-title__main'>{t('apartments.header.v2.title')}</span>
        <span className='page-title__subtitle'>{t('apartments.header.v2.subtitle')}</span>
      </div>
    );
  }, [isV2, t]);

  usePageTitleOverride(titleOverrideNode);

  return isV2 ? <PageApartmentListV2 {...props} /> : <PageApartmentListLegacy {...props} />;
};

export const App = () => {
  const store = useStore();
  const [loading, setLoading] = useState(true);
  const [i18nLoading, seti18nLoading] = useState(true);
  const { i18n } = useTranslation();
  const [jwtLoading, setJwtLoading] = useState(true);
  const prjInfo = useQuery(Client.GET_PROJECT_INFO, Client.GET_PROJECT_INFO_DEFAULT_OPTIONS(DetectUrl()));
  const skipCondition = window.location.pathname.includes('resetPassword');
  const [loadUserInfo, userInfo] = useLazyQuery(Client.GET_USER_INFO);
  const dispatch = useAppDispatch();

  const getProject = async () => {
    if (prjInfo && prjInfo.data && prjInfo.data.getProjectInfoByHost) {
      const project = { ...prjInfo.data.getProjectInfoByHost };
      store.setAssetsByObject(project);
      dispatch({ type: 'SET_PROJECT', payload: project });
      store.setDefaultLanguage(project.defaultLang);
      dispatch({ type: 'SET_LANGUAGES', payload: project.languages });
      setBaseUrl(prjInfo.data.getProjectInfoByHost.baseurl);
      dispatch({ type: 'SET_BASE_URL', payload: store.baseUrl });
      // @ts-ignore
      document.title = store.pageTitles?.followup;
      setLoading(false);
      await loadResources({
        id: project.id,
        displayName: project.displayName,
        languages: store.configLanguages,
      });
      seti18nLoading(false);
    }
  };

  useEffect(() => {
    getProject();
  }, [prjInfo]);

  useEffect(() => {
    if (userInfo && userInfo.data && userInfo.data.getUserByJWT) {
      const userRole = userInfo.data.getUserByJWT.role;
      const isDisabled = userInfo.data.getUserByJWT.isDisabled;
      /* 
        If the user has role "client" or user's account is disabled, force logout to prevent unauthorized access.
        This can happen when
        1. a user with role "client" logs in on another application
          that shares cookies: this application automatically recognizes the user via cookies,
          but does not allow login for users with role "client", so logout must be forced.
        2. user's account is disabled
      */
      if (userRole === 'client' || isDisabled) {
        setJwtLoading(false);
        forceLogoutUnauthorizedUser(store);
        return;
      }
      const previousRole = sessionStorage.getItem('loggedUserRole');
      if (previousRole && previousRole !== userRole) {
        setJwtLoading(false);
        forceLogoutUnauthorizedUser(store);
        return;
      }
      sessionStorage.setItem('loggedUserRole', userRole);
      setJwtLoading(false);
      const userLanguage = userInfo?.data?.getUserByJWT?.language;
      const userLocale = userInfo?.data?.getUserByJWT?.locale;
      store.setLoggedUserJwtData({ userData: userInfo.data.getUserByJWT, checkingJwt: false });
      dispatch({ type: 'SET_USER', payload: userInfo.data.getUserByJWT });
      dispatch(sliceClienti.actions.setLoggedUserId(userInfo.data.getUserByJWT.id));
      store.setSystemLanguage(userLanguage);
      dispatch({ type: 'SET_SYSTEM_LANGUAGE', payload: userLanguage });

      const isUserLanguageValid = store.configLanguages.includes(convertLanguage(userLanguage, userLocale));
      const isUserLanguageDifferent = i18n.language.split('-')[0] !== userLanguage;

      if (isUserLanguageValid && isUserLanguageDifferent) {
        const convertedLanguage = convertLanguage(userLanguage, userLocale);
        i18n.changeLanguage(convertedLanguage);
        moment.locale(convertedLanguage);
      }

      const isCurrentLanguageValid = store.configLanguages.find((lang: string) => lang.startsWith(i18n.language));
      if (!isCurrentLanguageValid) {
        i18n.changeLanguage(store.defaultLang);
      }
    } else if (userInfo?.error) {
      setJwtLoading(false);
    }
  }, [userInfo]);

  useEffect(() => {
    store.setCheckingJwt(true);
    if (store.projectId && !userInfo.called && !skipCondition)
      loadUserInfo(Client.GET_USER_INFO_DEFAULT_OPTIONS(store.projectId, skipCondition));
  }, [store.projectId]);

  if (loading || i18nLoading) return <CrmLoader hasBackdrop={false} loading z />;

  if (!store.baseUrl || !prjInfo)
    return (
      <div style={{ height: '100vh', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <h1>Iniziativa non valida</h1>
      </div>
    );

  return (
    <div className='wrapper'>
      <JwtChecker />
      {jwtLoading ? (
        <CrmLoader hasBackdrop={false} loading={true} z={true} />
      ) : (
        <SessionStore>
          <Router basename={store.baseUrl}>
            <CrmSnackbarFeedback />
            <CrmSnackbarAmber />
            <CrmSnackbarError />
            <Switch>
              <PrivateRoute exact path='/' ToRender={PageHome} title='title.home' />
              <PrivateRoute exact path='/clienti' ToRender={PageClients} title='title.clientManager' />
              <PrivateRoute path='/scheda-cliente/:id' ToRender={PageClientDetail} title='title.clientCard' goBack />
              <PrivateRoute path='/modifica-cliente/:id' ToRender={PageClientUpdate} title='title.updateClientCard' goBack />
              <PrivateRoute exact path='/aggiungi-cliente' ToRender={PageClientCreate} title='title.addClient' goBack />
              <PrivateRoute exact path='/clienti-legacy' ToRender={PageClientListLegacy} title='title.clientManager' />
              <PrivateRoute exact path='/appartamenti' ToRender={ApartmentsPageSwitch} title='title.apartments' />
              <PrivateRoute exact path='/scheda-appartamento/:id' ToRender={PageApparmentUpdate} title='title.apartmentCard' goBack />
              <PrivateRoute exact path='/crea-appartamenti' ToRender={PageApartmentCreate} title='title.createtList' goBack />
              <PrivateRoute exact path='/userInfoManager' ToRender={PageUserDetail} title='title.userInfoManager' goBack />
              <PrivateRoute exact path='/calendario' ToRender={PageCalendar} title='title.calendar' />
              <PrivateRoute exact path='/calendario/settings' ToRender={PageCalendarSettings} title='calendar.settings' goBack />
              <PrivateRoute exact path='/dettagli-calendario/:id' ToRender={PageCalendarDetail} title='title.appointmentDetails' goBack />
              <PrivateRoute path='/new-event/:id' ToRender={PageEventCreate} title='title.createAppointment' />
              <PrivateRoute path='/update-event/:id' ToRender={PageEventUpdate} title='title.updateAppointment' goBack />
              <PrivateRoute exact path='/accountManager' ToRender={PageUserManagement} title='title.vendorManager' goBack />
              <PrivateRoute exact path='/preventivi' ToRender={PageQuoteList} title='title.listaPreventivi' />
              <PrivateRoute path='/scheda-pertinenza/:id' ToRender={PagePertinenceUpdate} title='title.pertinenzaCard' goBack />
              <PrivateRoute path='/update-busy-slot/:id' ToRender={PageSlotUpdate} title='title.updateSlot' goBack />
              <PrivateRoute exact path='/gestioneProposta' ToRender={PageProposalManagement} title='title.gestioneProposta' />
              <PrivateRoute exact path='/documenti-hc' ToRender={PageDocuments} title='navbarTitle.documentiHC' />
              <Route exact path='/resetPassword' component={AggiornaConfermaPassword} />
              <Route exact path='/login' component={PageLogin} />
              <Route path='*' component={() => <Redirect to='/' />} />
            </Switch>
            <LogoutListener />
          </Router>
        </SessionStore>
      )}
    </div>
  );
};
